import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from './components/button/button.component';
import { ProgressSpinnerComponent } from './components/progress-spinner/progress-spinner.component';
import { CustomAlertComponent } from './components/custom-alert/custom-alert.component';
import { TitleComponent } from './components/title/title.component';
import { DynamicCalendarComponent } from './components/forms/dynamic-field/dynamic-calendar/dynamic-calendar.component';
import { CustomHeaderComponent } from './components/forms/dynamic-field/dynamic-calendar/custom-header/custom-header.component';
import { LocalizedDatePipe } from './pipes/localized-date.pipe';
import { DynamicDropdownComponent } from './components/forms/dynamic-field/dynamic-dropdown/dynamic-dropdown.component';
import { ArrayToStringPipe } from './pipes/array-to-string.pipe';
import { DynamicVerifiedTextComponent } from './components/forms/dynamic-field/dynamic-verified-text/dynamic-verified-text.component';
import { DynamicDropdownSearchComponent } from './components/forms/dynamic-field/dynamic-dropdown-search/dynamic-dropdown-search.component';
import { DynamicTextComponent } from './components/forms/dynamic-field/dynamic-text/dynamic-text.component';
import { BreadcrumbsComponent } from './components/breadcrumbs/breadcrumbs.component';
import { EnablePopupComponent } from './components/popup/enable-popup/enable-popup.component';
import { BasicListComponent } from './components/basic-list/basic-list.component';
import { DynamicRangeCalendarComponent } from './components/forms/dynamic-field/dynamic-range-calendar/dynamic-range-calendar.component';
import { DynamicTextAreaComponent } from './components/forms/dynamic-field/dynamic-text-area/dynamic-text-area.component';
import { CustomMatPaginatorIntl } from './classes/custom-mat-paginator-intl';
import { BasePopupComponent } from './components/popup/base-popup/base-popup.component';
import { TreeComponent } from './components/tree/tree.component';
import { MatRadioModule } from '@angular/material/radio';

import { EnabledPipe } from './pipes/enabled.pipe';

import { OverlayModule } from '@angular/cdk/overlay';
import { LayoutModule } from '@angular/cdk/layout';
import { CdkTreeModule } from '@angular/cdk/tree';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialogModule } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSliderModule } from '@angular/material/slider';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatNativeDateModule } from '@angular/material/core';
import { MatTableModule } from '@angular/material/table';
import {
	MatPaginatorIntl,
	MatPaginatorModule
} from '@angular/material/paginator';
import { MatStepperModule } from '@angular/material/stepper';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { MatToolbarModule } from '@angular/material/toolbar';
import { DynamicRadioGroupComponent } from './components/forms/dynamic-field/dynamic-radio-group/dynamic-radio-group.component';
import { MatSortModule } from '@angular/material/sort';
import { YesNoPipe } from './pipes/yes-no.pipe';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ImportExcelComponent } from './components/import-excel/import-excel.component';
import { GoogleMapsModule } from '@angular/google-maps';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { WaybillViewComponent } from './components/waybill-view/waybill-view.component';
import { NestedViewComponent } from './components/nested-view/nested-view.component';
import { ZeroToEmptyPipe } from './pipes/zero-to-empty.pipe';
import { CustomWarningComponent } from './components/custom-warning/custom-warning.component';
import { DynamicPropertyPipe } from './pipes/dynamic-property.pipe';
import { provideHttpClient } from '@angular/common/http';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';

const CDK_MODULES = [OverlayModule, LayoutModule, CdkTreeModule];

const MATERIAL_MODULES = [
	MatSidenavModule,
	MatButtonModule,
	MatIconModule,
	MatExpansionModule,
	MatProgressSpinnerModule,
	MatDialogModule,
	MatListModule,
	MatFormFieldModule,
	MatInputModule,
	MatDatepickerModule,
	MatNativeDateModule,
	MatSelectModule,
	MatCardModule,
	MatSnackBarModule,
	MatSliderModule,
	MatCardModule,
	MatMenuModule,
	MatBadgeModule,
	MatTableModule,
	MatPaginatorModule,
	MatStepperModule,
	MatCheckboxModule,
	MatTooltipModule,
	MatToolbarModule,
	MatTreeModule,
	MatRadioModule,
	MatSortModule,
	MatAutocompleteModule
];

const CUSTOM_COMPONENTS = [
	ButtonComponent,
	ProgressSpinnerComponent,
	CustomAlertComponent,
	DynamicVerifiedTextComponent,
	DynamicDropdownSearchComponent,
	DynamicTextComponent,
	TitleComponent,
	DynamicCalendarComponent,
	CustomHeaderComponent,
	LocalizedDatePipe,
	DynamicDropdownComponent,
	ArrayToStringPipe,
	BreadcrumbsComponent,
	EnabledPipe,
	EnablePopupComponent,
	BasicListComponent,
	DynamicTextAreaComponent,
	DynamicRangeCalendarComponent,
	BasePopupComponent,
	TreeComponent,
	DynamicRadioGroupComponent,
	YesNoPipe,
	ImportExcelComponent,
	WaybillViewComponent,
	NestedViewComponent,
	ZeroToEmptyPipe,
	CustomWarningComponent,
	DynamicPropertyPipe
];

@NgModule({
	declarations: [CUSTOM_COMPONENTS],
	imports: [
		CommonModule,
		RouterModule,
		ReactiveFormsModule,
		FontAwesomeModule,
		FormsModule,
		GoogleMapsModule,
		DragDropModule,
		LucideAngularModule,
		MATERIAL_MODULES
	],
	exports: [
		CommonModule,
		RouterModule,
		ReactiveFormsModule,
		LucideAngularModule,
		FontAwesomeModule,
		FormsModule,
		GoogleMapsModule,
		MATERIAL_MODULES,
		CUSTOM_COMPONENTS,
		CDK_MODULES
	],
	providers: [
		{
			provide: MatPaginatorIntl,
			useClass: CustomMatPaginatorIntl
		},
		provideHttpClient()
	]
})
export class SharedModule {}
